import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  addKybAdminDocument,
  updateKybRequirementDocument,
  updateKybRequirementValue,
  uploadKybDocumentFile,
  verifyKybRequirement,
} from "../../../shared/api/kyb";
import { getApiErrorMessage } from "../../../shared/lib";
import { AddressFormData, DirectorFormData, KybComplianceDetail, KybRequirement } from "../types";
import KybComplianceChecklistCard from "./KybComplianceChecklistCard";
import KybComplianceDocumentIssueFlyin from "./KybComplianceDocumentIssueFlyin";
import KybComplianceDocumentPreviewModal from "./KybComplianceDocumentPreviewModal";
import KybComplianceDocumentsCard from "./KybComplianceDocumentsCard";
import KybComplianceFlagRequirementModal from "./KybComplianceFlagRequirementModal";
import KybComplianceOwnershipCard from "./KybComplianceOwnershipCard";
import KybComplianceReEnterInformationModal from "./KybComplianceReEnterInformationModal";
import KybComplianceReUploadDocumentModal from "./KybComplianceReUploadDocumentModal";
import {
  ChecklistItem,
  FlagTargetKind,
  getRequirementEndpointKey,
  getRequirementStatus,
} from "./kybShowReviewShared";

const KybComplianceShowBottomSection = ({
  detail,
  disableDocumentAdd = false,
  isReadOnly = false,
  onRequirementReviewed,
}: {
  detail: KybComplianceDetail;
  disableDocumentAdd?: boolean;
  isReadOnly?: boolean;
  onRequirementReviewed?: () => void;
}) => {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>("");
  const [isDocumentListOpen, setIsDocumentListOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isDocumentIssueFlyinOpen, setIsDocumentIssueFlyinOpen] =
    useState(false);
  const [isFlagModalOpen, setIsFlagModalOpen] = useState(false);
  const [isReEnterModalOpen, setIsReEnterModalOpen] = useState(false);
  const [isReUploadDocumentModalOpen, setIsReUploadDocumentModalOpen] =
    useState(false);
  const [documentUploadMode, setDocumentUploadMode] = useState<
    "add" | "reupload"
  >("reupload");
  const [flagReason, setFlagReason] = useState("");
  const [flagTarget, setFlagTarget] = useState<{
    kind: FlagTargetKind;
    requirement: KybRequirement;
    person?: {
      targetId?: string;
      type: "director" | "ubo";
    };
  } | null>(null);
  const [reEnterTarget, setReEnterTarget] = useState<KybRequirement | null>(
    null,
  );
  const [reEnterPerson, setReEnterPerson] = useState<{
    targetId?: string;
    type: "director" | "ubo";
  } | null>(null);
  const [reUploadDocumentTarget, setReUploadDocumentTarget] =
    useState<KybRequirement | null>(null);
  const [reviewingStatus, setReviewingStatus] = useState<
    "APPROVED" | "REJECTED" | null
  >(null);
  const [isUpdatingValue, setIsUpdatingValue] = useState(false);
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  const documentRequirements = useMemo(
    () => detail.kycRequirements.filter((item) => item.type === "document"),
    [detail.kycRequirements],
  );
  const uploadedDocumentCounts = useMemo(
    () => ({
      pending: documentRequirements.filter(
        (item) =>
          Boolean(item.documentUrl) &&
          getRequirementStatus(item.status) === "PENDING",
      ).length,
      approved: documentRequirements.filter(
        (item) =>
          Boolean(item.documentUrl) &&
          getRequirementStatus(item.status) === "APPROVED",
      ).length,
      rejected: documentRequirements.filter(
        (item) =>
          Boolean(item.documentUrl) &&
          ["REJECTED", "QUERIED"].includes(getRequirementStatus(item.status)),
      ).length,
    }),
    [documentRequirements],
  );
  const activeDocument = useMemo(() => {
    if (!documentRequirements.length) return null;

    return (
      documentRequirements.find((item) => item.id === selectedDocumentId) ??
      documentRequirements[0]
    );
  }, [documentRequirements, selectedDocumentId]);
  const activeDocumentUrl = activeDocument?.documentUrl;
  const activeDocumentStatus = getRequirementStatus(activeDocument?.status);
  const isActiveDocumentFlagged = ["QUERIED", "REJECTED"].includes(
    activeDocumentStatus,
  );
  const activeDocumentIndex = activeDocument
    ? documentRequirements.findIndex((item) => item.id === activeDocument.id)
    : -1;
  const canGoToPreviousDocument = activeDocumentIndex > 0;
  const canGoToNextDocument =
    activeDocumentIndex >= 0 &&
    activeDocumentIndex < documentRequirements.length - 1;
  const selectDocumentAtIndex = (index: number) => {
    const document = documentRequirements[index];
    if (!document) return;

    setSelectedDocumentId(document.id);
  };

  useEffect(() => {
    if (!documentRequirements.length) {
      setSelectedDocumentId("");
      return;
    }

    const selectedExists = documentRequirements.some(
      (item) => item.id === selectedDocumentId,
    );

    if (!selectedExists) {
      setSelectedDocumentId(documentRequirements[0].id);
    }
  }, [documentRequirements, selectedDocumentId]);

  const personReviewRequirement = useMemo<KybRequirement>(
    () =>
      detail.kycRequirements.find(
        (requirement) =>
          requirement.rawKey === "directors" ||
          requirement.rawKey === "ubos" ||
          requirement.rawKey === "bvn" ||
          ["directors", "ubos", "bvn"].includes(
            requirement.key.trim().toLowerCase(),
          ),
      ) ?? {
        id: "bvn",
        key: "BVN",
        rawKey: "bvn",
        type: "field",
        status: "PENDING",
      },
    [detail.kycRequirements],
  );

  const checklistItems = useMemo<ChecklistItem[]>(() => {
    const items = detail.kycRequirements
      .filter((item) => item.type !== "document")
      .flatMap((requirement, index) => {
        const requirementKey = (
          requirement.rawKey || requirement.key
        ).trim().toLowerCase();
        const isDirectorRequirement =
          requirementKey === "directors" ||
          requirementKey === "bvn" ||
          Boolean(requirement.directors?.length);
        const isUboRequirement =
          requirementKey === "ubos" || Boolean(requirement.ubos?.length);
        const directors = requirement.directors ?? [];
        const ubos = requirement.ubos ?? [];

        if (isDirectorRequirement && directors.length > 0) {
          return directors.map((director, directorIndex) => {
            const personStatus = getRequirementStatus(
              director.status || requirement.status,
            );
            const name = [director.firstName, director.lastName]
              .filter(Boolean)
              .join(" ");

            return {
              id: `${requirement.id || requirement.rawKey || "bvn"}-director-${director.targetId || director.email || directorIndex}`,
              label: `Director ${directorIndex + 1}`,
              value:
                [name, director.bvnNumber].filter(Boolean).join(" • ") || "-",
              requirement,
              person: {
                targetId: director.targetId,
                type: "director" as const,
              },
              approved: personStatus === "APPROVED",
              flagged: ["QUERIED", "REJECTED"].includes(personStatus),
            };
          });
        }

        if (isUboRequirement && ubos.length > 0) {
          return ubos.map((ubo, uboIndex) => {
            const personStatus = getRequirementStatus(
              ubo.status || requirement.status,
            );
            const name = [ubo.firstName, ubo.lastName]
              .filter(Boolean)
              .join(" ");

            return {
              id: `${requirement.id || requirement.rawKey || "ubos"}-ubo-${ubo.targetId || ubo.email || uboIndex}`,
              label: `Ultimate Beneficial Owner ${uboIndex + 1}`,
              value:
                [name, ubo.bvnNumber].filter(Boolean).join(" • ") || "-",
              requirement,
              person: {
                targetId: ubo.targetId,
                type: "ubo" as const,
              },
              approved: personStatus === "APPROVED",
              flagged: ["QUERIED", "REJECTED"].includes(personStatus),
            };
          });
        }

        return [
          {
            id: requirement.id || requirement.rawKey || `requirement-${index}`,
            label: requirement.label || requirement.key,
            value: requirement.textValue || requirement.status || "-",
            requirement,
            approved: getRequirementStatus(requirement.status) === "APPROVED",
            flagged: ["QUERIED", "REJECTED"].includes(
              getRequirementStatus(requirement.status),
            ),
          },
        ];
      });

    const hasUboRequirement = detail.kycRequirements.some(
      (requirement) =>
        (requirement.rawKey || requirement.key).trim().toLowerCase() ===
          "ubos" || Boolean(requirement.ubos?.length),
    );
    const uboItems = hasUboRequirement
      ? []
      : detail?.ubos?.map((ubo, index) => {
          const personStatus = getRequirementStatus(ubo.status);
          const name = [ubo.firstName, ubo.lastName]
            .filter(Boolean)
            .join(" ");

          return {
            id: `ubo-${ubo.targetId || ubo.email || index}`,
            label: `Ultimate Beneficial Owner ${index + 1}`,
            value: [name, ubo.bvnNumber].filter(Boolean).join(" • ") || "-",
            requirement: personReviewRequirement,
            person: {
              targetId: ubo.targetId,
              type: "ubo" as const,
            },
            approved: personStatus === "APPROVED",
            flagged: ["QUERIED", "REJECTED"].includes(personStatus),
          };
      }) || [];

    return [...items, ...uboItems];
  }, [detail, personReviewRequirement]);

  const reviewRequirement = async (
    requirement: KybRequirement,
    status: "APPROVED" | "REJECTED",
    comment: string,
    kind: FlagTargetKind,
    person?: {
      targetId?: string;
      type: "director" | "ubo";
    },
  ) => {
    if (isReadOnly) return;

    const requirementKey = getRequirementEndpointKey(requirement);
    if (!requirementKey) return;

    setReviewingStatus(status);

    try {
      await verifyKybRequirement({
        businessId: detail.id,
        requirementKey,
        status,
        comment,
        targetId: person?.targetId,
        type: person?.type,
      });
      const subject = person
        ? person.type === "ubo"
          ? "UBO"
          : "Director"
        : kind === "information"
          ? "Information"
          : "Document";
      toast.success(
        status === "APPROVED"
          ? `${subject} approved successfully.`
          : `${subject} flagged successfully.`,
      );
      setIsFlagModalOpen(false);
      setFlagReason("");
      setFlagTarget(null);
      onRequirementReviewed?.();
    } catch (error) {
      const subject = person
        ? person.type === "ubo"
          ? "UBO"
          : "director"
        : kind === "information"
          ? "information"
          : "document";
      toast.error(
        getApiErrorMessage(
          error,
          status === "APPROVED"
            ? `Unable to approve ${subject}.`
            : `Unable to flag ${subject}.`,
        ),
      );
    } finally {
      setReviewingStatus(null);
    }
  };

  const reviewActiveDocument = async (
    status: "APPROVED" | "REJECTED",
    comment: string,
  ) => {
    if (!activeDocument) return;

    await reviewRequirement(activeDocument, status, comment, "document");
  };
  const openFlagModal = (
    requirement: KybRequirement | null | undefined,
    kind: FlagTargetKind,
    person?: {
      targetId?: string;
      type: "director" | "ubo";
    },
  ) => {
    if (isReadOnly) return;
    if (!requirement) return;

    setFlagTarget({
      kind,
      requirement,
      person,
    });
    setFlagReason("");
    setIsFlagModalOpen(true);
  };

  const handleFlagModalOpenChange = (open: boolean) => {
    if (isReadOnly) return;
    if (reviewingStatus !== null) return;
    setIsFlagModalOpen(open);
    if (!open) {
      setFlagReason("");
      setFlagTarget(null);
    }
  };

  const openReEnterModal = (
    requirement: KybRequirement,
    person?: { targetId?: string; type: "director" | "ubo" },
  ) => {
    if (isReadOnly) return;
    if (requirement.type === "document") return;

    setReEnterTarget(requirement);
    setReEnterPerson(person ?? null);
    setIsReEnterModalOpen(true);
  };

  const handleReEnterModalOpenChange = (open: boolean) => {
    if (isUpdatingValue) return;
    setIsReEnterModalOpen(open);
    if (!open) {
      setReEnterTarget(null);
      setReEnterPerson(null);
    }
  };

  const updateRequirementValue = async (
    payload: string | AddressFormData | DirectorFormData,
  ) => {
    if (!reEnterTarget) return;

    const requirementKey = getRequirementEndpointKey(reEnterTarget);
    if (!requirementKey) return;

    setIsUpdatingValue(true);

    try {
      if (typeof payload === "string") {
        await updateKybRequirementValue({
          businessId: detail.id,
          requirementKey,
          textValue: payload,
        });
      } else if ("street" in payload && "zipCode" in payload) {
        await updateKybRequirementValue({
          businessId: detail.id,
          requirementKey,
          address: payload,
        });
      } else {
        await updateKybRequirementValue({
          businessId: detail.id,
          requirementKey,
          director: payload,
          targetId: reEnterPerson?.targetId,
        });
      }
      toast.success("Information updated successfully.");
      setIsReEnterModalOpen(false);
      setReEnterTarget(null);
      setReEnterPerson(null);
      onRequirementReviewed?.();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to update information."),
      );
    } finally {
      setIsUpdatingValue(false);
    }
  };

  const openAddDocumentModal = () => {
    if (disableDocumentAdd || isReadOnly) return;

    setDocumentUploadMode("add");
    setReUploadDocumentTarget(null);
    setIsReUploadDocumentModalOpen(true);
  };

  const openReUploadDocumentModal = (
    requirement: KybRequirement | null | undefined,
  ) => {
    if (isReadOnly) return;
    if (!requirement || requirement.type !== "document") return;

    setDocumentUploadMode("reupload");
    setReUploadDocumentTarget(requirement);
    setIsReUploadDocumentModalOpen(true);
  };

  const handleReUploadDocumentModalOpenChange = (open: boolean) => {
    if (isUploadingDocument) return;
    setIsReUploadDocumentModalOpen(open);
    if (!open) setReUploadDocumentTarget(null);
  };

  const uploadDocument = async ({
    file,
    documentName,
  }: {
    file: File;
    documentName?: string;
  }) => {
    if (isReadOnly) return;

    const requirementKey =
      documentUploadMode === "reupload" && reUploadDocumentTarget
          ? getRequirementEndpointKey(reUploadDocumentTarget)
          : "";
    if (documentUploadMode === "reupload" && !requirementKey) return;

    setIsUploadingDocument(true);

    try {
      const uploadedDocument = await uploadKybDocumentFile(file);
      if (!uploadedDocument.url) {
        throw new Error("Unable to get uploaded document URL.");
      }

      if (documentUploadMode === "add") {
        await addKybAdminDocument({
          businessId: detail.id,
          document: {
            ...uploadedDocument,
            key: uploadedDocument.key || documentName?.trim() || file.name,
          },
        });
      } else {
        await updateKybRequirementDocument({
          businessId: detail.id,
          requirementKey,
          document: uploadedDocument,
        });
      }
      toast.success(
        documentUploadMode === "add"
          ? "Document added successfully."
          : "Document uploaded successfully.",
      );
      setIsReUploadDocumentModalOpen(false);
      setReUploadDocumentTarget(null);
      onRequirementReviewed?.();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Unable to upload document."),
      );
    } finally {
      setIsUploadingDocument(false);
    }
  };

  return (
    <section className="mt-4 grid items-start gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.95fr)] min-[1600px]:grid-cols-[0.78fr_1.35fr_1.05fr]">
      <div className="order-1 min-w-0 min-[1600px]:order-2">
        <KybComplianceDocumentsCard
          activeDocument={activeDocument}
          activeDocumentStatus={activeDocumentStatus}
          activeDocumentUrl={activeDocumentUrl}
          documentRequirements={documentRequirements}
          hasPreviewableDocument={Boolean(activeDocumentUrl)}
          isActiveDocumentFlagged={isActiveDocumentFlagged}
          isDocumentListOpen={isDocumentListOpen}
          requirementCounts={uploadedDocumentCounts}
          reviewingStatus={reviewingStatus}
          disableAddDocument={disableDocumentAdd}
          isReadOnly={isReadOnly}
          onDocumentListOpenChange={setIsDocumentListOpen}
          onOpenFlagModal={openFlagModal}
          onOpenAddDocument={openAddDocumentModal}
          onOpenReUploadDocument={openReUploadDocumentModal}
          onOpenIssueDetails={() => setIsDocumentIssueFlyinOpen(true)}
          onPreviewOpen={() => setIsPreviewOpen(true)}
          onReviewActiveDocument={reviewActiveDocument}
          onSelectedDocumentChange={setSelectedDocumentId}
        />
      </div>

      <div className="order-2 min-w-0 min-[1600px]:order-3">
        <KybComplianceChecklistCard
          checklistItems={checklistItems}
          reviewingStatus={reviewingStatus}
          isReadOnly={isReadOnly}
          onOpenFlagModal={openFlagModal}
          onOpenReEnterModal={openReEnterModal}
          onReviewRequirement={reviewRequirement}
        />
      </div>

      <div className="order-3 min-w-0 xl:col-span-2 min-[1600px]:order-1 min-[1600px]:col-span-1">
        <KybComplianceOwnershipCard detail={detail} />
      </div>

      <KybComplianceFlagRequirementModal
        open={isFlagModalOpen}
        flagReason={flagReason}
        flagTargetKind={flagTarget?.kind ?? "document"}
        isSubmitting={reviewingStatus === "REJECTED"}
        onOpenChange={handleFlagModalOpenChange}
        onFlagReasonChange={setFlagReason}
        onSubmit={() => {
          if (!flagTarget) return;
          reviewRequirement(
            flagTarget.requirement,
            "REJECTED",
            flagReason,
            flagTarget.kind,
            flagTarget.person,
          );
        }}
      />

      <KybComplianceDocumentPreviewModal
        open={isPreviewOpen}
        document={activeDocument}
        documentUrl={activeDocumentUrl}
        canGoToPrevious={canGoToPreviousDocument}
        canGoToNext={canGoToNextDocument}
        onClose={() => setIsPreviewOpen(false)}
        onGoToPrevious={() => selectDocumentAtIndex(activeDocumentIndex - 1)}
        onGoToNext={() => selectDocumentAtIndex(activeDocumentIndex + 1)}
      />

      <KybComplianceDocumentIssueFlyin
        open={isDocumentIssueFlyinOpen}
        document={activeDocument}
        onOpenChange={setIsDocumentIssueFlyinOpen}
        onApprove={() => {
          if (!activeDocument) return;
          setIsDocumentIssueFlyinOpen(false);
          reviewActiveDocument("APPROVED", "Approved");
        }}
        onReUpload={() => {
          setIsDocumentIssueFlyinOpen(false);
          openReUploadDocumentModal(activeDocument);
        }}
        onViewFull={
          activeDocumentUrl
            ? () => {
                setIsDocumentIssueFlyinOpen(false);
                setIsPreviewOpen(true);
              }
            : undefined
        }
      />

      <KybComplianceReEnterInformationModal
        open={isReEnterModalOpen}
        requirement={reEnterTarget}
        targetId={reEnterPerson?.targetId}
        isSubmitting={isUpdatingValue}
        onOpenChange={handleReEnterModalOpenChange}
        onSubmit={updateRequirementValue}
      />

      <KybComplianceReUploadDocumentModal
        open={isReUploadDocumentModalOpen}
        mode={documentUploadMode}
        document={reUploadDocumentTarget}
        isSubmitting={isUploadingDocument}
        onOpenChange={handleReUploadDocumentModalOpenChange}
        onSubmit={uploadDocument}
      />
    </section>
  );
};

export default KybComplianceShowBottomSection;
